import { z } from "zod";
import { CRAWL_VERDICTS, RAW_HTML_MAX_BYTES } from "../constants/index.js";

/**
 * 진실의 원천: harness/API-SPEC.md §8, harness/CRAWL-POLICY.md
 * apps/collector(수집기)가 scope=ingest API Key 로만 부르는 엔드포인트의 계약.
 */

export const SecuiCrawlRunPushSchema = z.object({
  started_at: z.string(),
  finished_at: z.string().nullable(),
  verdict: z.enum(CRAWL_VERDICTS),
  cases_found: z.number().int().nonnegative().nullable(),
  cases_new: z.number().int().nonnegative().nullable(),
  cases_changed: z.number().int().nonnegative().nullable(),
  swaps_matched: z.number().int().nonnegative().nullable(),
  error: z.string().nullable().optional(),
  evidence_r2_key: z.string().nullable().optional(),
});
export type SecuiCrawlRunPush = z.infer<typeof SecuiCrawlRunPushSchema>;

// 원문 그대로 — 가공·요약 금지 (harness/SOUL.md S3, harness/CRAWL-POLICY.md)
/**
 * 비어 있는 칸은 `null` 로 온다.
 *
 * 수집기는 그 칸을 **보고 왔다.** SECUI 화면에 그 칸이 있고 값이 없었다는 뜻이라,
 * "안 보냈다"(undefined)와 "봤는데 비어 있었다"(null)는 다른 사실이다. 특히
 * `change_hw_serial` 이 그렇다 — 비어 있다는 것은 **아직 교체품이 안 나왔다**는
 * 뜻이고, 그건 오류가 아니라 오늘의 상태다 (반증: 이걸 안 받아서 100건이 통째로
 * 거절됐다, 2026-09-09).
 */
const CollectedText = z.string().nullable().optional();

export const SecuiCasePushSchema = z.object({
  secui_case_no: z.string().min(1),
  secui_idx: CollectedText,
  kind: CollectedText,
  opened_at: CollectedText,
  hw_serial: CollectedText,
  change_hw_serial: CollectedText,
  model: CollectedText,
  channel_company: CollectedText,
  progress_status: CollectedText,
  ship_status: CollectedText,
  return_status: CollectedText,
  detail_text: CollectedText, // ★ 콘솔 로그 원문 — 가공 금지
  /**
   * ★ 수집 원문의 R2 키. 반드시 있어야 한다 — H3·H4 가 원문 대조에 쓴다.
   *
   * **`crawl/` 아래만 허용한다**(EVIDENCE-POLICY §4 의 키 규칙). 아무 키나 받으면
   * 수집 키를 가진 쪽이 증적 버킷의 다른 파일을 "수집 원문" 이라고 가리킬 수 있고,
   * 매처는 그 파일에 시리얼이 있는지만 보므로 **남의 로그를 근거로 교체가 만들어진다**
   * (반증 RT-23).
   */
  raw_r2_key: z.string().regex(/^crawl\/[^\s]+$/, "수집 원문은 crawl/ 아래여야 합니다"),
});
export type SecuiCasePush = z.infer<typeof SecuiCasePushSchema>;

/**
 * 한 번에 받는 케이스 배열. 상한은 배치 입고와 같은 이유다 (반증 RT-9).
 * 계약은 packages/shared 에만 둔다 — 라우터가 zod 를 직접 쓰기 시작하면
 * "계약은 여기 하나" 라는 말이 사실이 아니게 된다.
 */
export const SECUI_CASE_PUSH_MAX = 200;
export const SecuiCasePushListSchema = z.array(SecuiCasePushSchema).min(1).max(SECUI_CASE_PUSH_MAX);
export type SecuiCasePushList = z.infer<typeof SecuiCasePushListSchema>;

export const SecuiCrawlRunResponseSchema = z.object({ run_id: z.number().int().positive() });
export type SecuiCrawlRunResponse = z.infer<typeof SecuiCrawlRunResponseSchema>;

/**
 * 수집 결과를 받은 뒤의 보고 (harness/API-SPEC.md §8-1).
 *
 * `accepted` 는 저장한 SECUI 케이스 수, `matched` 는 그중 **교체 연결을 새로 만든** 수다.
 * 나머지는 왜 안 만들었는지 `skipped` 에 이유별로 센다 — 0건이 나왔을 때 그것이
 * "아무것도 없었다" 인지 "전부 거부됐다" 인지 구분되지 않으면, 조용한 실패가 된다.
 */
export const SecuiCasePushResponseSchema = z.object({
  accepted: z.number().int().nonnegative(),
  matched: z.number().int().nonnegative(),
  skipped: z.array(
    z.object({
      secui_case_no: z.string(),
      reason: z.string(),
      detail: z.string().optional(),
    }),
  ),
});
export type SecuiCasePushResponse = z.infer<typeof SecuiCasePushResponseSchema>;

/**
 * 수집 원문 업로드 (harness/API-SPEC.md §8-2).
 *
 * **키는 서버가 정한다.** 수집기가 키를 정해서 보내면, 수집 키를 가진 쪽이 증적 버킷의
 * 다른 파일을 가리킬 수 있다(반증 RT-23 이 그것이었다). 규칙으로 막는 것과 **정할 수 없게
 * 만드는 것**은 다르다 — 여기서는 후자를 택한다.
 */
export const SecuiRawPushSchema = z.object({
  /** 무엇을 받아 온 페이지인가. 키의 갈래가 된다 */
  kind: z.enum(["list", "detail"]),
  /** 상세면 SECUI 접수번호. 목록이면 비운다 — 지어내지 않는다 */
  secui_case_no: z.string().trim().min(1).nullable().optional(),
  /** 페이지 원문 그대로. 요약·가공해서 보내지 않는다 (SOUL S3) */
  html: z.string().min(1).max(RAW_HTML_MAX_BYTES),
});
export type SecuiRawPush = z.infer<typeof SecuiRawPushSchema>;

export const SecuiRawResponseSchema = z.object({
  /** 이 값을 그대로 `SecuiCasePush.raw_r2_key` 에 넣는다 */
  raw_r2_key: z.string(),
  bytes: z.number().int().nonnegative(),
  sha256: z.string(),
});
export type SecuiRawResponse = z.infer<typeof SecuiRawResponseSchema>;

/**
 * 사이트가 이미 아는 SECUI 케이스 (C84). 수집기가 매일 100건 상세를 전부 다시 받지 않도록,
 * "이 접수번호는 이 상태로 교체 시리얼까지 안다" 를 미리 알려 준다. 수집기는 목록의 상태가
 * 같고 교체 시리얼이 이미 있는 건만 건너뛴다 — 교체 시리얼이 비어 있으면 매일 다시 본다.
 */
export const SecuiKnownCaseSchema = z.object({
  secui_case_no: z.string(),
  hw_serial: z.string().nullable(),
  change_hw_serial: z.string().nullable(),
  progress_status: z.string().nullable(),
});
export type SecuiKnownCase = z.infer<typeof SecuiKnownCaseSchema>;

export const SecuiKnownResponseSchema = z.object({
  items: z.array(SecuiKnownCaseSchema),
});
export type SecuiKnownResponse = z.infer<typeof SecuiKnownResponseSchema>;
